import { useEffect, useState } from 'react'
import Analytics from 'utils/googleAnalytics'

import { CardType } from 'types/deck'
import { RecommendationCutCardType } from 'types/recommendations'

import CardService from 'services/card.service'
import ToastService from 'services/toast.service'

import { useAppSelector } from 'redux/hooks'
import { selectCommanderCards, selectMaindeckCards } from 'redux/deck/selectors'

import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'
import PhatDropdown from 'components/formElements/PhatDropdown'

import styles from './edhRecsForm.module.scss'

type Props = {
  onSubmitted: () => void
  onRequestFailed: (message?: string) => void
  // send back the search object to the parent in case anything wants to be done with it
  onResponseRecieved: (response: CardType[]) => void
  className?: string
  alreadyHasResults?: boolean
  noAutoSearch?: boolean
}

const EdhRecFrom = ({ onSubmitted, onRequestFailed, onResponseRecieved, noAutoSearch }: Props) => {
  const [selectedCardType, setSelectedCardType] = useState('all')
  const [selectRecs, setSelectRecs] = useState<boolean>(true)
  const [recsResults, setRecsResults] = useState(new Array<CardType>())
  const [cutsResults, setCutsResults] = useState(new Array<RecommendationCutCardType>())

  const commanders = useAppSelector(selectCommanderCards)
  const maindeck = useAppSelector(selectMaindeckCards)

  const handleSearch = (e?: React.SyntheticEvent, cardType = selectedCardType) => {
    e?.preventDefault()

    // prettier-ignore
    if (!commanders.length) return ToastService.create('Your deck needs a commander before we can get EDHRECs', 'EDHREC Search', 'warning')

    onSubmitted() // let the parent know we're about to submit the search

    let recsSize = '1 - 50'

    if (maindeck.length > 50) recsSize = '51 - 100'
    if (maindeck.length > 100) recsSize = '101 - 150'
    if (maindeck.length > 150) recsSize = '151 - 200'
    if (maindeck.length > 200) recsSize = '200+'

    Analytics.signalEvent(`Recs size ${recsSize}`)

    CardService.edhRecs(commanders, maindeck)
      .then(res => {
        setRecsResults(res.recs)
        setCutsResults(res.cuts)

        handleFilterResults(cardType, selectRecs ? res.recs : res.cuts)
      })
      .catch(err => {
        console.error(err)

        onRequestFailed(err.error)
      })
  }

  // Passing the local results as an argument here so handleSearch can pass the updated results without rerendering first
  const handleFilterResults = (filter: string, localResults: CardType[] = selectRecs ? recsResults : cutsResults) => {
    setSelectedCardType(filter)

    if (!localResults || !localResults.length) return handleSearch(undefined, filter)
    if (filter === 'all') return onResponseRecieved(localResults)

    const filteredResults = localResults.filter(card => {
      if (card.types.includes(filter)) return true
      if (card.front?.types.includes(filter)) return true
      if (card.back?.types.includes(filter)) return true

      return false
    })

    onResponseRecieved(filteredResults)
  }

  const handleToggleRecsCuts = (value: boolean) => {
    setSelectRecs(value)
    handleFilterResults(selectedCardType, value ? recsResults : cutsResults)
  }

  useEffect(() => {
    if (noAutoSearch) return

    handleSearch()
  }, [])

  return (
    <>
      <form onSubmit={handleSearch} className={styles.form}>
        <PhatDropdown
          className={styles.dropdown}
          menuClassName={styles.menu}
          value={selectedCardType}
          options={[
            { label: 'All Card Types', onClick: () => handleFilterResults('all'), id: 'all' },
            { label: 'Artifact', onClick: () => handleFilterResults('Artifact'), id: 'Artifact' },
            { label: 'Creature', onClick: () => handleFilterResults('Creature'), id: 'Creature' },
            {
              label: 'Enchantment',
              onClick: () => handleFilterResults('Enchantment'),
              id: 'Enchantment',
            },
            { label: 'Instant', onClick: () => handleFilterResults('Instant'), id: 'Instant' },
            { label: 'Land', onClick: () => handleFilterResults('Land'), id: 'Land' },
            {
              label: 'Planeswalker',
              onClick: () => handleFilterResults('Planeswalker'),
              id: 'Planeswalker',
            },
            { label: 'Sorcery', onClick: () => handleFilterResults('Sorcery'), id: 'Sorcery' },
          ]}
        />
        <PhatDropdown
          menuClassName={styles.menu}
          value={selectRecs ? 'recs' : 'cuts'}
          options={[
            { label: 'Recs', onClick: () => handleToggleRecsCuts(true), id: 'recs' },
            { label: 'Cuts', onClick: () => handleToggleRecsCuts(false), id: 'cuts' },
          ]}
        />
        <PhatButton color="green" className={styles.submit}>
          <Icon name="search" /> Search
        </PhatButton>
      </form>
      {!selectRecs && <p className={styles.cutsMessage}>Cuts are sorted by unpopularity based on EDHREC data.</p>}
    </>
  )
}

export default EdhRecFrom
